/* @refresh skip */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { addDays, format, isSameDay, startOfWeek } from 'date-fns';
import { formatDate, formatMonthNameYear, formatWeekday } from './utils/format';

const WEEK_OPTS = { weekStartsOn: 1 };
const dateKey = (date) => format(date, 'yyyy-MM-dd');

const ensureDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export default function WeekCalendarView({
  incomingEvents = [],
  reorderEvents = [],
  onIncomingClick,
  onReorderClick,
}) {
  const today = new Date();
  const [anchorDate, setAnchorDate] = useState(today);
  const [expandedDay, setExpandedDay] = useState(dateKey(today));
  const weekStart = useMemo(() => startOfWeek(anchorDate, WEEK_OPTS), [anchorDate]);
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, idx) => addDays(weekStart, idx)),
    [weekStart],
  );

  const eventMap = useMemo(() => {
    const map = new Map();
    const push = (dateInput, payload, type) => {
      const date = ensureDate(dateInput);
      if (!date) return;
      const key = dateKey(date);
      if (!map.has(key)) map.set(key, { incoming: [], reorder: [] });
      map.get(key)[type].push(payload);
    };
    incomingEvents.forEach((event) => push(event.date, event.resource || event, 'incoming'));
    reorderEvents.forEach((event) => push(event.date, event.resource || event, 'reorder'));
    return map;
  }, [incomingEvents, reorderEvents]);

  const weekBuckets = weekDays.map((day) => ({
    date: day,
    key: dateKey(day),
    incoming: eventMap.get(dateKey(day))?.incoming || [],
    reorder: eventMap.get(dateKey(day))?.reorder || [],
  }));

  const toggleDay = (key) => setExpandedDay((prev) => (prev === key ? '' : key));
  const navigateWeek = (direction) => setAnchorDate((prev) => addDays(prev, direction * 7));
  const resetWeek = () => setAnchorDate(today);

  const renderDetails = (items, type) =>
    items.map((item, idx) => (
      <ListItemButton
        key={`${type}-${idx}-${item?.sku || item?.po_id || idx}`}
        onClick={() => {
          if (type === 'incoming') onIncomingClick?.(item);
          else onReorderClick?.(item);
        }}
        sx={{ borderRadius: 1 }}
      >
        <ListItemText
          primary={
            type === 'incoming'
              ? item.product_name || item.sku || `Inbound #${idx + 1}`
              : item.product_name || item.sku || `Reorder #${idx + 1}`
          }
          secondary={
            type === 'incoming'
              ? `Qty ${item.inbound_qty ?? '—'} · PO ${item.po_id || '—'}`
              : `Recommend ${item.recommended_order_qty ?? '—'} pcs`
          }
        />
      </ListItemButton>
    ));

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1">
          Week of {formatMonthNameYear(weekBuckets[0]?.date || today)}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={resetWeek}>
            This week
          </Button>
          <IconButton size="small" onClick={() => navigateWeek(-1)} aria-label="Previous week">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => navigateWeek(1)} aria-label="Next week">
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Stack spacing={0}>
        {weekBuckets.map(({ date, key, incoming, reorder }, idx) => {
          const isToday = isSameDay(date, today);
          const activities = incoming.length + reorder.length;
          return (
            <Box key={key}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ py: 1.25, cursor: 'pointer' }}
                onClick={() => toggleDay(key)}
              >
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" color={isToday ? 'primary.main' : 'text.primary'}>
                    {formatWeekday(date, { long: true })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(date)}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {incoming.length} inbound
                  </Typography>
                  <Typography variant="body2" color="warning.main">
                    {reorder.length} reorder
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activities ? `${activities} activities` : 'No activity'}
                  </Typography>
                  <IconButton size="small" edge="end" onClick={(event) => { event.stopPropagation(); toggleDay(key); }}
                    aria-label={`${expandedDay === key ? 'Collapse' : 'Expand'} day ${key}`}>
                    {expandedDay === key ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Stack>
              </Stack>
              <Collapse in={expandedDay === key} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2, pb: 2 }}>
                  {activities === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Nothing scheduled today.
                    </Typography>
                  )}
                  {incoming.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>
                        Inbound shipments
                      </Typography>
                      <List dense>{renderDetails(incoming, 'incoming')}</List>
                    </>
                  )}
                  {incoming.length > 0 && reorder.length > 0 && <Divider sx={{ my: 1 }} />}
                  {reorder.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>
                        Reorder alerts
                      </Typography>
                      <List dense>{renderDetails(reorder, 'reorder')}</List>
                    </>
                  )}
                </Box>
              </Collapse>
              {idx < weekBuckets.length - 1 && <Divider />}
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}
